'use client';

import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import Image from 'next/image';
import { useState } from 'react';

import { AvailableForText } from '@/app/(dashboard)/settings/backup-policies/supportedResourceTypesText';
import type { StepProps } from '@/components/wizard/StepProps';
import { validateName } from '@/components/wizard/renameEntityName';

import type { ZeroEtlPolicyEditState } from './ZeroEtlPolicyCreationFlow';
import { ZERO_ETL_SUPPORTED_RESOURCE_TYPES } from './zeroEtlPolicyTypes';

export const ZeroEtlPolicyNameSelection = (
  props: StepProps<ZeroEtlPolicyEditState>
) => {
  const [type, setType] = useState<'aws-glue'>('aws-glue');
  const next = () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  return (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <Stack width='400px' gap='40px'>
        <NameSelection
          name={props.wizardState.name}
          setName={(name) =>
            props.setWizardState({ ...props.wizardState, name })
          }
        />
        <TypeSelection type={type} setType={setType} />
        <AvailableForText resourceTypes={ZERO_ETL_SUPPORTED_RESOURCE_TYPES} />
        <Button
          data-testid='create-zero-etl-policy-name-continue-btn'
          variant='contained'
          disabled={!validateName(props.wizardState.name) || !type}
          onClick={next}
          sx={{ height: '48px' }}
        >
          Continue
          <i className='material-symbols-arrow-right-alt ml-[8px]' />
        </Button>
      </Stack>
    </Stack>
  );
};

const NameSelection = ({
  name,
  setName,
}: {
  name: string;
  setName: (name: string) => void;
}) => {
  return (
    <FormControl size='small' sx={{ flex: 1 }}>
      <FormLabel>Name</FormLabel>
      <OutlinedInput
        inputProps={{
          'data-testid': 'create-zero-etl-policy-name-input',
        }}
        autoFocus
        sx={{ padding: '8px' }}
        placeholder='Use a descriptive name, such as "Production DBs"'
        onChange={(e) => setName(e.target.value)}
        value={name}
      />
    </FormControl>
  );
};

const TypeSelection = ({
  type,
  setType,
}: {
  type: 'aws-glue';
  setType: (type: 'aws-glue') => void;
}) => {
  return (
    <FormControl size='small' sx={{ flex: 1 }}>
      <FormLabel>Type</FormLabel>
      <Select
        sx={{ padding: '8px' }}
        size='medium'
        value={type}
        onChange={(e) => setType(e.target.value)}
      >
        <MenuItem value='aws-glue'>
          <Stack direction='row' alignItems='center' gap='12px'>
            <Image
              src='/statics/images/lakehouse/Glue.png'
              alt='AWS Glue'
              width={24}
              height={24}
            />
            AWS Glue
          </Stack>
        </MenuItem>
      </Select>
    </FormControl>
  );
};
